import { useParams } from 'react-router-dom';
import { DiscoveryProvider } from './context/DiscoveryContext';
import { DiscoveryWizard } from './components/DiscoveryWizard';
import { DiscoveryBackground } from './components/DiscoveryBackground';
import { getIndustryBySlug, getSubcategoryBySlug } from '@/data/discovery/industries';
import { Helmet } from 'react-helmet-async';
import { createBreadcrumbSchema, createCollectionPageSchema, createOrganizationSchema } from '@/config/seo';
import { getCanonicalUrl } from '@/config/site';

const DiscoveryPage = () => {
  const { industrySlug, subcategorySlug } = useParams();

  // If URL params are provided, start at that step
  let initialIndustry;
  let initialSubcategory;

  if (industrySlug) {
    initialIndustry = getIndustryBySlug(industrySlug);
  }

  if (industrySlug && subcategorySlug) {
    const result = getSubcategoryBySlug(industrySlug, subcategorySlug);
    if (result) {
      initialIndustry = result.industry;
      initialSubcategory = result.subcategory;
    }
  }

  const path = subcategorySlug
    ? `/discover/${industrySlug}/${subcategorySlug}`
    : industrySlug
      ? `/discover/${industrySlug}`
      : '/discover';
  const canonicalUrl = getCanonicalUrl(path);
  const title = initialSubcategory
    ? `${initialSubcategory.name} Voice AI Ideas | Help Genie Discover`
    : initialIndustry
      ? `${initialIndustry.name} Voice AI Solutions | Help Genie Discover`
      : 'Discover Voice AI Solutions | Help Genie';
  const description = initialSubcategory
    ? `Explore voice AI ideas for ${initialSubcategory.name.toLowerCase()} businesses and find Help Genie workflows, playbooks, and automation paths that match that use case.`
    : initialIndustry
      ? `Discover Help Genie voice AI solutions tailored for ${initialIndustry.name.toLowerCase()} businesses, including industry-specific workflows and recommended playbooks.`
      : 'Discover voice AI solutions tailored to your industry and use case with Help Genie. Answer a few questions to find the best automation path for your business.';
  const breadcrumbItems = [
    { name: 'Home', url: getCanonicalUrl('/') },
    { name: 'Discover', url: getCanonicalUrl('/discover') },
  ];

  if (initialIndustry) {
    breadcrumbItems.push({
      name: initialIndustry.name,
      url: getCanonicalUrl(`/discover/${initialIndustry.slug}`),
    });
  }

  if (initialIndustry && initialSubcategory) {
    breadcrumbItems.push({
      name: initialSubcategory.name,
      url: canonicalUrl,
    });
  }

  const structuredData = [
    createBreadcrumbSchema(breadcrumbItems),
    createCollectionPageSchema(title, description, canonicalUrl),
    createOrganizationSchema(),
  ];

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        {structuredData.map((entry, index) => (
          <script key={`discovery-structured-data-${index}`} type="application/ld+json">
            {JSON.stringify(entry)}
          </script>
        ))}
      </Helmet>

      {/* Full-screen immersive experience */}
      <div className="fixed inset-0 bg-black overflow-hidden">
        <DiscoveryProvider
          initialIndustry={initialIndustry}
          initialSubcategory={initialSubcategory}
        >
          <DiscoveryBackground />
          {/* Content */}
          <div className="relative z-10 h-full">
            <DiscoveryWizard />
          </div>
        </DiscoveryProvider>
      </div>
    </>
  );
};

export default DiscoveryPage;
